import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import Badges from './Badges';
import Expander from '../Expander';
import ExtLink from '../ExtLink';
import { FiPlus } from 'react-icons/fi';
import Frame from 'react-frame-component';
import Link from '../Link';
import { PlaceholderShimmerBackground } from 'utils/fragments';
import { PlaceholderText } from '../Placeholders';
import { SoftButton } from '../Button';
import Stack from '../Stack';
import TikTokPostsNoDataMessage from '../TikTokPostsNoDataMessage';
import dayjs from 'dayjs';
import { getPathFromNode } from 'utils/helpers';
import { getThumbnail } from 'utils/api/instagram.api';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import useTikTokThumbnail from 'utils/hooks/useTikTokThumbnail';
import weekOfYear from 'dayjs/plugin/weekOfYear';

dayjs.extend(weekOfYear);

const TopPostPlaceholderThumbnail = styled.div`
    background-color: ${({ theme }) => theme.gray_5};
    border-radius: ${({ theme }) => theme.borderRadius_md};
    min-height: var(--post-thumbnail-height, 20.33rem);
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;

    ${({ isLoading }) => (isLoading ? PlaceholderShimmerBackground : '')}
`;

function TopPostPlaceholder() {
    return (
        <Stack $space=".25rem">
            <TopPostPlaceholderThumbnail isLoading={true} />
            <div css="text-align: right;">
                <PlaceholderText />
            </div>
        </Stack>
    );
}

const frameStyles = { border: 0, position: 'absolute', inset: 0 };

function EmbeddedImage({ thumbnail_url }) {
    return (
        <Frame
            style={frameStyles}
            width="100%"
            height="100%"
            mountTarget="#mount-point"
            initialContent={`
                <!DOCTYPE html>
                <html>
                    <head>
                        <meta charset="utf-8">
                        <style>
                            body {
                                background-color: #E9E9E9;
                                margin: 0;
                                padding: 0;
                                width: 100%;
                                height: 100%;
                                overflow: hidden;
                            }
                            #mount-point {
                                position: absolute;
                                inset: 0;
                            }
                        </style>
                    </head>
                    <body>
                            <div id="mount-point"></div>
                    </body>
                </html>
            `}
        >
            <div
                style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <img
                    src={thumbnail_url}
                    style={{
                        objectFit: 'cover',
                        width: '100%',
                        height: '100%',
                    }}
                />
            </div>
        </Frame>
    );
}

function DirectImage({ thumbnail_url }) {
    return (
        <div
            style={{
                position: 'absolute',
                inset: 0,
                backgroundColor: '#E9E9E9',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <img
                src={thumbnail_url}
                alt=""
                style={{
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                }}
            />
        </div>
    );
}

const PostLink = styled(ExtLink)`
    background: transparent;
    position: absolute;
    inset: 0;
    &:hover,
    &:focus-visible {
        background: rgba(255, 255, 255, 0.25);
    }
`;

const ScreenReaderOnly = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
`;

export function TopPostTikTokThumbnail({ url }) {
    const { thumbnailUrl, isLoading, isError } = useTikTokThumbnail(url);

    if (!thumbnailUrl) {
        return (
            <TopPostPlaceholderThumbnail
                as="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                isLoading={isLoading}
            >
                {isError ? (
                    <div className="text-center text-muted-foreground text-sm px-4 max-w-[10rem]">
                        Could not load this post.
                    </div>
                ) : null}
            </TopPostPlaceholderThumbnail>
        );
    }

    return (
        <TopPostPlaceholderThumbnail isLoading={isLoading}>
            <EmbeddedImage thumbnail_url={thumbnailUrl} />
            <PostLink
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                mixpanelContext="TOP_POST_LINK"
            >
                <ScreenReaderOnly>view post on TikTok</ScreenReaderOnly>
            </PostLink>
        </TopPostPlaceholderThumbnail>
    );
}

export function TopPostInstagramThumbnail({ url }) {
    const shortcode = url?.match(/\/p\/([A-Za-z0-9_-]+)/)?.[1];

    const queryFn = useCallback(async () => {
        if (!shortcode) return null;
        return await getThumbnail(shortcode);
    }, [shortcode]);

    const thumbnailQuery = useQuery({
        queryKey: ['instagram-thumbnail', shortcode],
        queryFn,
        enabled: !!shortcode,
        cacheTime: /** 1 hour */ 60 * 60 * 1000,
        staleTime: /** 1 hour */ 60 * 60 * 1000,
        retry: (failureCount, error) => {
            // Don't retry 404s - post doesn't exist or is unavailable
            if (error?.status === 404) return false;
            // Retry up to 2 times for transient errors (503, network issues, 500)
            return failureCount < 2;
        },
    });

    const thumbnail_url = useMemo(() => {
        if (!thumbnailQuery.data) return null;
        return URL.createObjectURL(thumbnailQuery.data);
    }, [thumbnailQuery.data]);

    useEffect(() => {
        const currentUrl = thumbnail_url;
        return () => {
            if (currentUrl) {
                URL.revokeObjectURL(currentUrl);
            }
        };
    }, [thumbnail_url]);

    if (!shortcode) {
        return (
            <TopPostPlaceholderThumbnail
                as="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
            />
        );
    }

    if (!thumbnail_url) {
        return (
            <TopPostPlaceholderThumbnail
                as="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                isLoading={thumbnailQuery.isLoading}
            >
                {thumbnailQuery.isError ? (
                    <div className="text-center text-muted-foreground text-sm px-4 max-w-[10rem]">
                        Could not load this post.
                    </div>
                ) : null}
            </TopPostPlaceholderThumbnail>
        );
    }

    return (
        <TopPostPlaceholderThumbnail isLoading={thumbnailQuery.isLoading}>
            <DirectImage thumbnail_url={thumbnail_url} />
            <PostLink
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                mixpanelContext="TOP_POST_LINK"
            >
                <ScreenReaderOnly>view post on Instagram</ScreenReaderOnly>
            </PostLink>
        </TopPostPlaceholderThumbnail>
    );
}

const PostMetricRow = styled.div`
    font-variant-numeric: tabular-nums;
    font-size: 0.75rem;
    font-weight: 500;
    color: ${({ theme }) => theme.gray_600};
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;

    ${({ theme, $isSortedRow }) =>
        $isSortedRow && `&& { color: ${theme.purple_500}; }`}
`;
const PostMetricLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
`;
const PostMetric = styled.span`
    color: ${({ theme }) => theme.gray_900};
    text-transform: uppercase;
`;
const PostMetricText = styled.span`
    color: ${({ theme }) => theme.gray_900};
`;
const BrandLink = styled(Link)`
    color: ${({ theme }) => theme.purple_1};
    text-decoration: none;

    &:hover,
    &:focus-visible {
        text-decoration: underline;
    }
`;

function TikTokTopPostNewMetrics({
    post,
    sortOption,
    visibleOrderedMetrics,
    dateFormat = 'MMM D, YYYY',
}) {
    return (
        <Stack
            $space=".75rem"
            css="display: flex; flex-direction: column; min-height: 21.33rem;"
        >
            <Stack $space=".5rem">
                <TopPostTikTokThumbnail
                    url={post.url}
                    key={`${sortOption.sortBy}-${post.url}`}
                />
                <Badges post={post} />
            </Stack>
            <Stack $space=".4375rem" css="margin-top: auto;">
                {visibleOrderedMetrics.map(
                    ({ key, tableHeadColumnLabel, format }, idx, ref) => (
                        <Fragment key={key}>
                            <PostMetricRow>
                                <PostMetricLabel>
                                    {key === 'last_week_views'
                                        ? 'Views'
                                        : tableHeadColumnLabel}
                                </PostMetricLabel>
                                {key === 'create_timestamp' ? (
                                    <PostMetricText>
                                        {dayjs
                                            .unix(post[key])
                                            .format(dateFormat)}
                                    </PostMetricText>
                                ) : key === 'brandName' ? (
                                    <PostMetricText
                                        css={`
                                            text-transform: capitalize;
                                            line-height: 1.2;
                                        `}
                                    >
                                        <BrandLink
                                            to={getPathFromNode({
                                                id: post.brand,
                                                type: 'brand',
                                                name: post.brandName,
                                            })}
                                        >
                                            {post.brandName}
                                        </BrandLink>
                                    </PostMetricText>
                                ) : (
                                    <PostMetric>{format(post[key])}</PostMetric>
                                )}
                            </PostMetricRow>
                            {ref.length > 1 && idx < ref.length - 1 ? (
                                <div
                                    css={`
                                        border-bottom: solid 1px
                                            ${({ theme }) => theme.gray_200};
                                    `}
                                />
                            ) : null}
                        </Fragment>
                    ),
                )}
            </Stack>
        </Stack>
    );
}

export function InstagramTopPostNewMetrics({
    post,
    sortOption,
    visibleOrderedMetrics,
    dateFormat = 'MMM D, YYYY',
}) {
    return (
        <Stack
            $space=".75rem"
            css="display: flex; flex-direction: column; min-height: 21.33rem;"
        >
            <Stack $space=".5rem">
                <TopPostInstagramThumbnail
                    url={post.url}
                    key={`${sortOption.sortBy}-${post.url}`}
                />
                <Badges post={post} />
            </Stack>
            <Stack $space=".4375rem" css="margin-top: auto;">
                {visibleOrderedMetrics.map(
                    ({ key, tableHeadColumnLabel, format }, idx, ref) => (
                        <Fragment key={key}>
                            <PostMetricRow>
                                <PostMetricLabel>
                                    {key === 'last_week_likes'
                                        ? 'Likes'
                                        : tableHeadColumnLabel}
                                </PostMetricLabel>
                                {key === 'create_timestamp' ? (
                                    <PostMetricText>
                                        {dayjs
                                            .unix(post[key])
                                            .format(dateFormat)}
                                    </PostMetricText>
                                ) : key === 'brandName' ? (
                                    <PostMetricText
                                        css={`
                                            text-transform: capitalize;
                                            line-height: 1.2;
                                        `}
                                    >
                                        <BrandLink
                                            to={getPathFromNode({
                                                id: post.brand,
                                                type: 'brand',
                                                name: post.brandName,
                                            })}
                                        >
                                            {post.brandName}
                                        </BrandLink>
                                    </PostMetricText>
                                ) : (
                                    <PostMetric>{format(post[key])}</PostMetric>
                                )}
                            </PostMetricRow>
                            {ref.length > 1 && idx < ref.length - 1 ? (
                                <div
                                    css={`
                                        border-bottom: solid 1px
                                            ${({ theme }) => theme.gray_200};
                                    `}
                                />
                            ) : null}
                        </Fragment>
                    ),
                )}
            </Stack>
        </Stack>
    );
}

const PostsExpander = styled(Expander)`
    display: grid;
    gap: 1.75rem 1rem;
    grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
    align-items: stretch;
`;

const NoDataMessageWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.gray_3};
    min-height: 20.33rem;
`;

function Post({
    posts,
    isLoading,
    isError,
    displayQty = 5,
    numToDisplay: controlledNumToDisplay,
    onNumToDisplayChange,
    sortOption,
    visibleOrderedMetrics,
    NoDataMessage = TikTokPostsNoDataMessage,
    PostComponent = TikTokTopPostNewMetrics,
    ShowMoreButton,
    HideAllButton,
    ButtonsWrapper,
}) {
    const [internalNumToDisplay, setInternalNumToDisplay] =
        useState(displayQty);

    const numToDisplay =
        controlledNumToDisplay !== undefined
            ? controlledNumToDisplay
            : internalNumToDisplay;

    const setNumToDisplay = (val) => {
        if (controlledNumToDisplay === undefined) {
            setInternalNumToDisplay(val);
        }
        if (onNumToDisplayChange) {
            onNumToDisplayChange(val);
        }
    };

    useEffect(() => {
        if (controlledNumToDisplay === undefined) {
            setInternalNumToDisplay(displayQty);
        }
    }, [displayQty, controlledNumToDisplay]);

    return (
        <>
            {posts && posts.length ? (
                <PostsExpander>
                    {posts.slice(0, numToDisplay).map((post) => (
                        <PostComponent
                            post={post}
                            sortOption={sortOption}
                            key={`${post.id}-${post.brandName || '0'}`}
                            visibleOrderedMetrics={visibleOrderedMetrics}
                        />
                    ))}
                </PostsExpander>
            ) : isLoading ? (
                <PostsExpander>
                    <TopPostPlaceholder />
                </PostsExpander>
            ) : isError ? (
                <NoDataMessageWrapper>
                    Could not load top posts.
                </NoDataMessageWrapper>
            ) : (
                <NoDataMessageWrapper>
                    <NoDataMessage />
                </NoDataMessageWrapper>
            )}
            {ButtonsWrapper ? (
                <ButtonsWrapper>
                    {posts && posts.length > displayQty && (
                        <ShowMoreButton
                            onClick={() =>
                                setNumToDisplay(numToDisplay + displayQty)
                            }
                            disabled={numToDisplay >= posts.length}
                        />
                    )}
                    {numToDisplay > displayQty && (
                        <HideAllButton
                            onClick={() => setNumToDisplay(displayQty)}
                        />
                    )}
                </ButtonsWrapper>
            ) : (
                <div css="display: flex; align-items: center;">
                    {posts && posts.length > displayQty ? (
                        ShowMoreButton ? (
                            <ShowMoreButton
                                onClick={() =>
                                    setNumToDisplay(numToDisplay + displayQty)
                                }
                                disabled={numToDisplay >= posts.length}
                            />
                        ) : (
                            <SoftButton
                                IconLeft={FiPlus}
                                size="sm"
                                onClick={() =>
                                    setNumToDisplay(numToDisplay + displayQty)
                                }
                                isDisabled={numToDisplay >= posts.length}
                                mixpanelContext="SHOW_MORE_POSTS"
                            >
                                Show more posts
                            </SoftButton>
                        )
                    ) : null}
                    {numToDisplay > displayQty ? (
                        HideAllButton ? (
                            <HideAllButton
                                onClick={() => setNumToDisplay(displayQty)}
                            />
                        ) : (
                            <>
                                <span
                                    css={`
                                        border-left: solid 1px
                                            ${({ theme }) => theme.gray_5};
                                        min-height: 16px;
                                        margin: 0px 12px;
                                    `}
                                />
                                <SoftButton
                                    size="sm"
                                    onClick={() => setNumToDisplay(displayQty)}
                                >
                                    Hide All
                                </SoftButton>
                            </>
                        )
                    ) : null}
                </div>
            )}
        </>
    );
}

export default Post;
